import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { log } from "../lib/logger";
import { useAuth } from "./AuthContext";

const FavCtx = createContext({
  favSet: new Set(),
  toggle: () => {},
  refresh: () => {},
  loading: false,
});

// Encode item_type + item_id into a single Set key so O(1) lookup on catalog cards is easy.
const favKey = (item_type, item_id) => `${item_type}::${item_id}`;

/**
 * Loads the current user's favorites once on mount (and on user change) and keeps a
 * Set of "type::id" keys in memory so every FavoriteButton on the page can check its
 * state without an extra API call. Toggle mutates optimistically and syncs on error.
 */
export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [favSet, setFavSet] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setFavSet(new Set()); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/me/favorites");
      setFavSet(new Set((data.items || []).map((f) => favKey(f.item_type, f.item_id))));
    } catch (err) {
      log.warn("[Favorites] load failed:", err);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = useCallback(async (item_type, item_id) => {
    if (!user) return false;
    const key = favKey(item_type, item_id);
    const wasFav = favSet.has(key);
    // Optimistic update
    setFavSet((prev) => {
      const next = new Set(prev);
      if (wasFav) next.delete(key); else next.add(key);
      return next;
    });
    try {
      if (wasFav) {
        await api.delete(`/favorites/${item_type}/${item_id}`);
      } else {
        await api.post("/favorites", { item_type, item_id });
      }
      return !wasFav;
    } catch (err) {
      // Revert on failure
      log.warn("[Favorites] toggle failed:", err);
      setFavSet((prev) => {
        const next = new Set(prev);
        if (wasFav) next.add(key); else next.delete(key);
        return next;
      });
      throw err;
    }
  }, [user, favSet]);

  return (
    <FavCtx.Provider value={{ favSet, toggle, refresh, loading }}>
      {children}
    </FavCtx.Provider>
  );
}

export const useFavorites = () => useContext(FavCtx);
export const isFav = (favSet, item_type, item_id) => favSet.has(favKey(item_type, item_id));
